/* eslint-disable max-lines */

import type { DocumentResponse } from '@backend/schemas'
import { useQueryClient } from '@tanstack/react-query'
import type { Column } from '@tanstack/react-table'
import type { TFunction } from 'i18next'
import {
  AlertCircleIcon,
  FileIcon,
  LoaderIcon,
  PersonStandingIcon,
  SortAsc,
  SortDesc,
  Trash2Icon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UseIndividualsServiceGetV1IndividualsByIndividualIdKeyFn } from '@/api/client/queries'
import { useErrandsServiceGetV1IndividualsByIndividualIdErrandsKey } from '@/api/client/queries/common'
import { useDeleteDocument } from '@/api/hooks/useDeleteDocument'
import {
  type DocumentUploadStatus,
  useDocumentUploadStatus,
} from '@/api/hooks/useDocumentUploadStatus'
import { fileDeleteToast } from '@/components/molecules/custom-async-toasts'
import { ResponsiveTabs } from '@/components/molecules/responsive-tabs'
import { ColumnActions } from '@/components/organisms/data-table/actions-column'
import { Button } from '@/components/ui/button'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { TabsContent } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePreviewDocument, usePreviewDocumentFiles } from '@/hooks/useDocumentPreview'
import { useIndividuals } from '@/hooks/useIndividuals'
import { cn, DateUtils } from '@/lib/utils'
import { getDocumentIcon } from '@/utils/icon.utils'

import { ConfirmDialog } from '../confirm-dialog'
import type { CustomColumnDef } from '../data-table'
import MarkdownRenderer from '../MarkdownRenderer'
import TimelineVertical, { type ExtendedTimelineEvent } from '../timeline/timeline-vertical'

/**
 * How long a document may sit in a non-final status before we treat it as stuck
 * and offer removal. The pipeline writes to the document on every phase, so
 * `updatedAt` standing still this long means nothing is progressing — a job
 * that died without writing a terminal status. Well above the slowest normal
 * run so a healthy document is never offered for deletion mid-processing.
 */
const STUCK_AFTER_MS = 30 * 60 * 1000

export const useDocumentsTableColumns: (
  documents: DocumentResponse[],
) => CustomColumnDef<DocumentResponse>[] = documents => {
  const { selectedIndividualId } = useIndividuals()
  const deleteDocument = useDeleteDocument()
  const uploadStatuses = useDocumentUploadStatus(
    selectedIndividualId ?? '',
    documents.filter(doc => doc.status !== 'done').map(doc => doc.id),
  )
  const queryClient = useQueryClient()
  const previousUploadStatuses = useRef<Record<string, DocumentUploadStatus>>({})

  const documentsRef = useRef(documents)
  documentsRef.current = documents

  const { t } = useTranslation()

  // A stuck document by definition stops emitting updates, so nothing would
  // re-render the row when it crosses STUCK_AFTER_MS. Tick slowly while
  // something is unfinished so the remove action appears without a reload.
  const hasUnfinishedDocuments = documents.some(doc => doc.status !== 'done')
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!hasUnfinishedDocuments) return

    const interval = setInterval(() => setNow(Date.now()), 60_000)

    return () => clearInterval(interval)
  }, [hasUnfinishedDocuments])

  useEffect(() => {
    if (!selectedIndividualId) return

    for (const [id, status] of Object.entries(uploadStatuses)) {
      const previousStatus = previousUploadStatuses.current[id]

      // Both terminal statuses matter: `done` brings in the generated data, and
      // `error` is what flips the row out of its loading state so the delete
      // action becomes reachable (see getIsError).
      if (
        previousStatus &&
        previousStatus !== status &&
        (status === 'done' || status === 'error')
      ) {
        queryClient.invalidateQueries({
          queryKey: UseIndividualsServiceGetV1IndividualsByIndividualIdKeyFn({
            individualId: selectedIndividualId,
          }),
        })

        const doc = documentsRef.current.find(d => d.id === id)

        if (doc?.errands && doc.errands.length > 0) {
          queryClient.invalidateQueries({
            queryKey: [useErrandsServiceGetV1IndividualsByIndividualIdErrandsKey],
          })
        }
      }
    }

    previousUploadStatuses.current = uploadStatuses
  }, [selectedIndividualId, uploadStatuses])

  const onRequestDeleteDocument = (id: string) => {
    if (!selectedIndividualId) return
    const deletionPromise = deleteDocument.mutateAsync({
      documentId: id,
      individualId: selectedIndividualId,
    })
    fileDeleteToast(deletionPromise)
  }

  // The realtime channel reports the failure before the refetch it triggers
  // lands, so a row must be treated as failed on either source of truth —
  // otherwise it keeps rendering as "uploading" and the user can't remove it.
  const getIsError = (doc: DocumentResponse): boolean =>
    doc.status === 'error' || uploadStatuses[doc.id] === 'error'

  const getIsLoading = (doc: DocumentResponse): boolean => doc.status !== 'done' && !getIsError(doc)

  // Still processing on paper, but nothing has moved for STUCK_AFTER_MS — the
  // shape a silently-died pipeline takes, since a crashed job leaves the status
  // untouched instead of writing `error`.
  const getIsStuck = (doc: DocumentResponse): boolean => {
    if (!getIsLoading(doc)) return false
    const updatedAt = doc.updatedAt ? new Date(doc.updatedAt).getTime() : Number.NaN

    return !Number.isNaN(updatedAt) && now - updatedAt > STUCK_AFTER_MS
  }

  const renderSortHeader = (
    column: Column<DocumentResponse>,
    label: string,
    className?: string,
  ) => {
    const currentSort = column.getIsSorted()

    const sortColumn = () => {
      if (currentSort === 'asc') column.toggleSorting(true)
      else if (currentSort === 'desc') column.clearSorting()
      else column.toggleSorting(false)
    }

    const icon =
      currentSort === 'desc' ? (
        <SortDesc className="h-4 w-4" />
      ) : currentSort === 'asc' ? (
        <SortAsc className="h-4 w-4" />
      ) : null

    return (
      <Button
        className={cn(className)}
        onClick={sortColumn}
        variant={currentSort ? 'secondary' : 'ghost'}
      >
        {label}
        {icon}
      </Button>
    )
  }

  return [
    {
      accessorKey: 'expanded',
      cell: ({ row }) => {
        const DocumentIcon = getDocumentIcon(row.original.type)
        const isUploading = getIsLoading(row.original)
        const isError = getIsError(row.original)

        return (
          <div
            className={cn(
              `size-8 bg-layer-2 group-hover:bg-layer-3 rounded-sm flex items-center justify-center`,
              {
                'bg-error-bg': isError,
                'bg-info-bg': isUploading,
              },
            )}
          >
            {isUploading ? (
              <LoaderIcon className="size-4 text-info animate-spin" />
            ) : isError ? (
              <AlertCircleIcon className="size-4 text-error" />
            ) : (
              <DocumentIcon className={cn('size-4 text-text-primary')} />
            )}
          </div>
        )
      },
      columnClassName: 'hidden @3xl/table:table-cell',
      enableHiding: false,
      enableResizing: false,
      header: '',
      maxSize: 48,
      minSize: 48,
      size: 48,
    },
    {
      accessorKey: 'name',
      cell: ({ cell }) => {
        const isUploading = getIsLoading(cell.row.original)
        const isError = getIsError(cell.row.original)

        const liveStatus = uploadStatuses[cell.row.original.id]

        return (
          <div className="text-left @3xl/table:pl-4 max-w-[70vw] wrap-anywhere">
            <div>{cell.getValue<string>()}</div>
            <div className="@3xl/table:hidden mt-1 text-text-secondary max-w-[70vw]">
              {cell.row.original.instance}
            </div>
            {cell.row.original.issuanceAt && (
              <div className="@3xl/table:hidden mt-2 whitespace-nowrap text-xs text-text-secondary font-medium">
                {DateUtils.formatRelativeDate(new Date(cell.row.original.issuanceAt), t)}
              </div>
            )}

            {isUploading && (
              <div className="text-info animate-pulse">
                {t('documentsTable.uploadingLabel', { context: liveStatus })}
              </div>
            )}
            {isError && (
              <div className="text-error">
                {t('documentsTable.uploadingLabel', { context: 'error' })}
              </div>
            )}
          </div>
        )
      },
      enableHiding: false,
      header: ({ column }) =>
        renderSortHeader(column, t('documentsTable.columns.documentName'), 'pl-0 @3xl/table:pl-4'),
      // TODO: Should be 30vw when in lg:
      // size: '30vw',
      size: '60vw' as unknown as number,
      sortingFn: 'alphanumeric',
    },
    {
      accessorKey: 'instance',
      cell: ({ cell }) => {
        return <div className="text-left pl-4">{cell.getValue<string>()}</div>
      },
      columnClassName: 'hidden @3xl/table:table-cell',
      header: ({ column }) => renderSortHeader(column, t('documentsTable.columns.instance')),
      size: '50ch' as unknown as number,
      sortingFn: 'alphanumeric',
    },
    {
      accessorKey: 'issuanceAt',
      cell: ({ cell, row }) => {
        const date = cell.getValue<string>() ? new Date(cell.getValue<string>()) : undefined
        const isLoading = getIsLoading(row.original)

        if (isLoading) return null

        return (
          date && (
            <span className="text-left whitespace-nowrap block pl-4">
              {DateUtils.formatRelativeDate(date, t)}
            </span>
          )
        )
      },
      columnClassName: 'hidden @3xl/table:table-cell',
      header: ({ column }) => renderSortHeader(column, t('documentsTable.columns.issuedAt')),
    },
    {
      cell: ({ row }) => {
        const isError = getIsError(row.original)

        if (isError || getIsLoading(row.original)) {
          // A failed document can go immediately; one that merely looks busy
          // has to prove it is stuck first, so a healthy in-progress document
          // is never offered for deletion mid-pipeline.
          if (!isError && !getIsStuck(row.original)) return null

          return (
            <DocumentIncompleteActions
              isError={isError}
              onDelete={() => onRequestDeleteDocument(row.original.id)}
            />
          )
        }

        return (
          <DocumentActions
            document={row.original}
            isExpanded={row.getIsExpanded()}
            onDelete={() => onRequestDeleteDocument(row.original.id)}
          />
        )
      },
      columnClassName: 'w-px p-2 @3xl/table:p-5',
      enableHiding: false,
      id: 'actions',
    },
  ] satisfies CustomColumnDef<DocumentResponse>[]
}

// ─── Row actions ──────────────────────────────────────────────────────────────

/**
 * Actions for a document that never finished processing — either flagged
 * `error` or stalled long enough to count as stuck. Preview and the expanded
 * row are meaningless here (nothing rendered yet, no generated data), so
 * removal is the only action offered, as a visible button and a dropdown item.
 */
const DocumentIncompleteActions = ({
  isError,
  onDelete,
}: {
  isError: boolean
  onDelete(): void
}) => {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)

  return (
    <ColumnActions
      dropDownMenuContent={
        <DropdownMenuItem destructive onSelect={() => setConfirmOpen(true)}>
          <Trash2Icon />
          {t('documentsTable.actions.delete')}
        </DropdownMenuItem>
      }
      showClickableButton={false}
    >
      <div onClick={e => e.stopPropagation()} role="presentation">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={t('documentsTable.actions.delete')}
              className="hover:bg-layer-1 hidden @3xl/table:inline-flex text-error hover:text-error"
              onClick={() => setConfirmOpen(true)}
              size="icon"
              variant="ghost"
            >
              <Trash2Icon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('documentsTable.actions.delete')}</TooltipContent>
        </Tooltip>
        <ConfirmDialog
          description={
            isError
              ? t('documentsTable.deleteFailedDocumentDescription')
              : t('documentsTable.deleteStuckDocumentDescription')
          }
          onConfirm={onDelete}
          onOpenChange={setConfirmOpen}
          open={confirmOpen}
          variant="destructive"
        />
      </div>
    </ColumnActions>
  )
}

type DocumentActionsProps = {
  document: DocumentResponse
  isExpanded: boolean
  onDelete(): void
}

const DocumentActions = ({ document, isExpanded, onDelete }: DocumentActionsProps) => {
  const { t } = useTranslation()
  const previewDocument = usePreviewDocument()
  const previewDocumentFiles = usePreviewDocumentFiles()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const openPreview = () => {
    // If the doc has multiple files OR a combined PDF, render the multi-source
    // preview with a segmented control: [Combined, File 1, File 2, ...] in
    // `order`. Otherwise fall back to single-source for plain single-file docs.
    const hasMultiple = document.files.length > 1
    const hasCombined = Boolean(document.combinedFilePath)

    if (hasMultiple || hasCombined) {
      previewDocumentFiles(document, document.combinedFilePath)

      return
    }

    const primary = document.files[0]
    if (primary) previewDocument(primary.filePath, primary.mimeType)
  }

  return (
    <ColumnActions
      dropDownMenuContent={
        <>
          {/* The small-screen stand-in for the file icon beside it, which is hidden below `lg`.
              A VIEWPORT breakpoint, not the `@3xl/table` container query the rest of the table
              uses: dropdown content is portaled to document.body, so it has no `@container/table`
              ancestor to query and a container variant there never matches — which is how this
              item ended up visible alongside the icon at every width. Both sides use the same
              viewport breakpoint so they stay exact complements and exactly one is ever shown. */}
          <DropdownMenuItem className="lg:hidden" onSelect={openPreview}>
            {t('documentsTable.actions.openDocument')}
          </DropdownMenuItem>
          <DropdownMenuItem destructive onSelect={() => setConfirmOpen(true)}>
            <Trash2Icon />
            {t('documentsTable.actions.delete')}
          </DropdownMenuItem>
        </>
      }
      isExpanded={isExpanded}
    >
      <div onClick={e => e.stopPropagation()} role="presentation">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="hover:bg-layer-1 hidden lg:inline-flex"
              onClick={openPreview}
              size="icon"
              variant="ghost"
            >
              <FileIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('documentsTable.actions.openDocument')}</TooltipContent>
        </Tooltip>
        <ConfirmDialog
          onConfirm={onDelete}
          onOpenChange={setConfirmOpen}
          open={confirmOpen}
          variant="destructive"
        />
      </div>
    </ColumnActions>
  )
}

export const renderExpandedRow = (data: DocumentResponse, t: TFunction) => {
  const descriptions = {
    ...data.categories.reduce(
      (acc, summary) => ({
        ...acc,
        [summary.category ?? '_']: summary.description ?? '',
      }),
      {},
    ),
  }

  return (
    <div className="overflow-x-auto space-y-8 [&_>_div]:space-y-2.5 max-w-[80vw] @3xl/table:max-w-none">
      <div>
        <h4>{t('documentsTable.sections.summariesLabel')}</h4>
        <p className="text-sm text-text-secondary max-w-prose">
          {t('documentsTable.sections.summariesDescription')}
        </p>
        <ResponsiveTabs
          breakpoint="@3xl/table"
          defaultValue={Object.keys(data.summaries)[0]}
          items={Object.keys(data.summaries).map(key => ({
            label: t(`funkitySummary_${key as 'brief'}`),
            value: key,
          }))}
        >
          {Object.entries(data.summaries).map(([key, value]) => (
            <TabsContent className="p-4 bg-layer-2 w-fit rounded-md" key={key} value={key}>
              <MarkdownRenderer className="max-w-prose">{value}</MarkdownRenderer>
            </TabsContent>
          ))}
        </ResponsiveTabs>
      </div>

      {data.categories.length > 0 && (
        <div>
          <h4>{t('documentsTable.sections.categoriesLabel')}</h4>
          <p className="text-sm text-text-secondary max-w-prose">
            {t('documentsTable.sections.categoriesDescription')}
          </p>
          <ResponsiveTabs
            breakpoint="@3xl/table"
            defaultValue={Object.keys(descriptions)[0]}
            items={Object.keys(descriptions).map(key => ({
              label: t(`funkityCategory_${key as 'home'}`),
              value: key,
            }))}
          >
            {Object.entries(descriptions).map(([key, value]) => (
              <TabsContent className="p-4 bg-layer-2 w-fit rounded-md" key={key} value={key}>
                <MarkdownRenderer className="max-w-prose">{value}</MarkdownRenderer>
              </TabsContent>
            ))}
          </ResponsiveTabs>
        </div>
      )}

      {data.activities.length > 0 && (
        <div>
          <h4>{t('documentsTable.sections.activitiesLabel')}</h4>
          <p className="text-sm text-text-secondary max-w-prose pb-4">
            {t('documentsTable.sections.activitiesDescription')}
          </p>
          <TimelineVertical
            events={data.activities.map(activity => ({
              ...activity,
              date: activity.date,
              documents: [] satisfies ExtendedTimelineEvent['documents'],
            }))}
          />
        </div>
      )}

      {data.issuers.length > 0 && (
        <div>
          <h4>{t('documentsTable.sections.issuersLabel')}</h4>
          <div className="flex items-center gap-6 flex-wrap">
            {data.issuers.map(issuer => {
              return (
                <div className="flex items-center gap-4" key={issuer.id ?? ''}>
                  <PersonStandingIcon className="text-text-secondary" />
                  <div className="space-y-0.5">
                    <p className="font-medium">{issuer.name}</p>
                    {issuer.role && (
                      <p className="text-text-secondary">
                        {t(('funkityIssuer_' + issuer.role) as 'funkityIssuer_physician')}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
